import type { CSSProperties, HTMLAttributes } from "react";
import { Kicker } from "../../ui/v2/Kicker";
import { scaledFont } from "@/styles/scaledFont";

export interface ConnectionPerson {
  name: string;
  /** One line about them today. */
  line: string;
  /**
   * The closest person of the day — the one warm dot on the screen. At most
   * one. @default false
   */
  primary?: boolean;
}

export interface ConnectionsListProps extends HTMLAttributes<HTMLDivElement> {
  /** The block's kicker — `t('…')`, never a literal. */
  title: string;
  /**
   * Already sorted by tier, with the tier-1 person flagged `primary` — the
   * ordering is the caller's, as in
   * `docs/design/handoff/design/templates/today/today-screen.jsx`.
   */
  people: ReadonlyArray<ConnectionPerson>;
}

const BLOCK: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
};

const ROW: CSSProperties = {
  display: "flex",
  gap: 12,
  alignItems: "baseline",
};

/* The dot is baseline-aligned with the name and then nudged 2px up to sit on
 * its x-height rather than its baseline. `--d-ramp-3` on the primary person is
 * the only warm pixel in the block. */
const DOT: CSSProperties = {
  width: 5,
  height: 5,
  borderRadius: "50%",
  flex: "none",
  transform: "translateY(-2px)",
};

const NAME: CSSProperties = { font: "var(--t-ui)", color: "var(--d-fg)" };

const LINE: CSSProperties = {
  font: scaledFont("500 13px/19px var(--font-body)"),
  color: "var(--d-fg-2)",
  textWrap: "pretty",
};

/**
 * "In your connections" — the people the day moves with, closest first.
 * Presentational: the tap target for a person is `ClosestRow`, not this list.
 *
 * Ported from `ConnectionsList` in
 * `docs/design/handoff/design/components/today/ConnectionsList.jsx`.
 */
export function ConnectionsList({
  title,
  people,
  className,
  style,
  ...rest
}: ConnectionsListProps): JSX.Element {
  return (
    <div className={className} style={{ ...BLOCK, ...style }} {...rest}>
      <Kicker>{title}</Kicker>
      {people.map((person) => (
        <div key={person.name} data-person-row style={ROW}>
          <span
            data-person-dot
            aria-hidden="true"
            style={{
              ...DOT,
              background: person.primary ? "var(--d-ramp-3)" : "var(--d-fg-4)",
            }}
          />
          <span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <span style={NAME}>{person.name}</span>
            <span style={LINE}>{person.line}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

export default ConnectionsList;
